import { useState } from "react";
import { Loader2, Save, X, Trash2, UploadCloud } from "lucide-react";
import { api, formatError } from "@/lib/api";

const inputCls =
  "w-full rounded-lg border border-gold/20 bg-ink px-4 py-3 text-ivory placeholder:text-stone2/60 outline-none transition-colors focus:border-gold";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-[0.16em] text-gold-subtle";

const TYPES = ["Piso", "Casa", "Chalet", "Villa", "Ático", "Apartamento", "Finca", "Terreno", "Local", "Otro"];

const PropertyForm = ({ initial, onSaved, onCancel }) => {
  const isNew = !initial;
  const [f, setF] = useState({
    title: initial?.title || "",
    operation: initial?.operation || "venta",
    property_type: initial?.property_type || "Piso",
    price: initial?.price ?? "",
    location: initial?.location || "",
    bedrooms: initial?.bedrooms ?? "",
    bathrooms: initial?.bathrooms ?? "",
    area: initial?.area ?? "",
    description: initial?.description || "",
    featuresText: (initial?.features || []).join(", "),
    images: initial?.images || [],
    featured: initial?.featured || false,
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setErr("");
    try {
      const fd = new FormData();
      files.forEach((file) => fd.append("files", file));
      const { data } = await api.post("/admin/upload", fd);
      setF((v) => ({ ...v, images: [...v.images, ...data.images] }));
    } catch (error) {
      setErr(formatError(error.response?.data?.detail) || "No se pudieron subir las fotos.");
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeImage = (i) => setF((v) => ({ ...v, images: v.images.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    const payload = {
      title: f.title,
      operation: f.operation,
      property_type: f.property_type,
      price: Number(f.price) || 0,
      location: f.location,
      bedrooms: Number(f.bedrooms) || 0,
      bathrooms: Number(f.bathrooms) || 0,
      area: Number(f.area) || 0,
      description: f.description,
      features: f.featuresText.split(",").map((s) => s.trim()).filter(Boolean),
      images: f.images,
      featured: f.featured,
      visible: true,
    };
    try {
      if (isNew) await api.post("/properties", payload);
      else await api.put(`/properties/${initial.id}`, payload);
      onSaved();
    } catch (error) {
      setErr(formatError(error.response?.data?.detail) || "No se pudo guardar la propiedad.");
    }
    setSaving(false);
  };

  return (
    <form onSubmit={submit} data-testid="property-form" className="rounded-2xl border border-gold/25 bg-ink2/90 p-6 md:p-8">
      <h3 className="font-serif text-2xl text-ivory">{isNew ? "Nueva propiedad" : `Editar — ${initial.ref}`}</h3>

      <div className="mt-6 grid gap-5 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className={labelCls}>Título *</label>
          <input required value={f.title} onChange={set("title")} placeholder="Ej.: Piso luminoso junto al mar" data-testid="property-title-input" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Operación *</label>
          <select value={f.operation} onChange={set("operation")} data-testid="property-operation-select" className={inputCls}>
            <option value="venta">Venta</option>
            <option value="alquiler">Alquiler</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Tipo</label>
          <select value={f.property_type} onChange={set("property_type")} data-testid="property-type-select-form" className={inputCls}>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>{f.operation === "alquiler" ? "Precio alquiler (€/mes) *" : "Precio de venta (€) *"}</label>
          <input required type="number" min="0" value={f.price} onChange={set("price")} placeholder="0" data-testid="property-price-input" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Ubicación</label>
          <input value={f.location} onChange={set("location")} placeholder="Ej.: Alicante" data-testid="property-location-input" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Habitaciones</label>
          <input type="number" min="0" value={f.bedrooms} onChange={set("bedrooms")} placeholder="0" data-testid="property-bedrooms-input" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Baños</label>
          <input type="number" min="0" value={f.bathrooms} onChange={set("bathrooms")} placeholder="0" data-testid="property-bathrooms-input" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Superficie (m²)</label>
          <input type="number" min="0" value={f.area} onChange={set("area")} placeholder="0" data-testid="property-area-input" className={inputCls} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Descripción</label>
          <textarea rows={4} value={f.description} onChange={set("description")} placeholder="Describe la propiedad…" data-testid="property-description-input" className={inputCls} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Características (separadas por comas)</label>
          <input value={f.featuresText} onChange={set("featuresText")} placeholder="Piscina, Garaje, Aire acondicionado" data-testid="property-features-input" className={inputCls} />
        </div>

        <div className="md:col-span-2">
          <label className={labelCls}>Fotos</label>
          <label
            htmlFor="property-images-input"
            data-testid="property-images-dropzone"
            className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gold/30 bg-ink/60 px-6 py-8 text-center transition-colors hover:border-gold/60"
          >
            {uploading ? <Loader2 size={26} className="animate-spin text-gold" /> : <UploadCloud size={26} className="text-gold" />}
            <span className="text-sm font-medium text-ivory">
              {uploading ? "Subiendo fotos…" : "Toca para añadir fotos"}
            </span>
            <span className="text-xs text-stone2">Desde tu móvil u ordenador, varias a la vez · JPG, PNG o WebP</span>
          </label>
          <input id="property-images-input" type="file" accept="image/*" multiple onChange={onFiles} data-testid="property-images-input" className="hidden" />
          {f.images.length > 0 && (
            <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
              {f.images.map((im, i) => (
                <div key={i} className="group relative overflow-hidden rounded-lg border border-gold/25">
                  <img src={im.startsWith("http") || im.startsWith("/api/") ? im : `/api/files/${im}`} alt="" className="aspect-square w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    data-testid={`property-remove-image-${i}`}
                    title="Quitar foto"
                    className="absolute right-1.5 top-1.5 rounded-full bg-ink/90 p-1.5 text-red-400 transition-colors hover:bg-red-500/20"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <label className="flex cursor-pointer items-center gap-3 md:col-span-2">
          <input type="checkbox" checked={f.featured} onChange={(e) => setF((v) => ({ ...v, featured: e.target.checked }))} data-testid="property-featured-checkbox" className="h-4 w-4 accent-[#D4AF37]" />
          <span className="text-sm text-ivory/85">Mostrar en propiedades destacadas de la portada</span>
        </label>
      </div>

      {err && (
        <p className="mt-4 text-sm text-red-400" data-testid="property-form-error">
          {err}
        </p>
      )}

      <div className="mt-8 flex gap-3">
        <button
          type="submit"
          disabled={saving || uploading}
          data-testid="property-save-button"
          className="btn-gold-metallic flex items-center gap-2 rounded-full px-7 py-3 text-xs font-bold uppercase tracking-[0.18em] text-ink disabled:opacity-60"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Guardar
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid="property-cancel-button"
          className="flex items-center gap-2 rounded-full border border-gold/30 px-7 py-3 text-xs font-bold uppercase tracking-[0.18em] text-gold transition-colors hover:bg-gold/10"
        >
          <X size={15} /> Cancelar
        </button>
      </div>
    </form>
  );
};

export default PropertyForm;
